import { DatePipe, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { ReactiveFormsModule, Validators, NonNullableFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroCheck, heroPlus, heroTrash } from '@ng-icons/heroicons/outline';
import { NgpSelectPortal } from 'ng-primitives/select';
import { finalize, map, of, switchMap } from 'rxjs';
import type { Observable } from 'rxjs';
import { CoreBusinessApiService } from '../../core/api/core-business-api.service';
import type { InvitationSetupResponse, SetupRole } from '../../core/api/core-onboarding-api.service';
import { CoreOnboardingApiService } from '../../core/api/core-onboarding-api.service';
import type { DiscoverableWorkspace } from '../../core/api/generated/models/DiscoverableWorkspace';
import { AuthStore } from '../../core/auth/auth.store';
import { PostHogService } from '../../core/services/posthog.service';
import {
  UiBadge,
  UiButton,
  UiCheckbox,
  UiInput,
  UiLabel,
  UiOptionRow,
  UiPanelCard,
  UiSelect,
  UiSelectDropdown,
  UiSelectOption,
  ToastrService,
} from '../../shared/ui';

const TEAM_SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201+'] as const;

const FOCUS_OPTIONS = [
  'Atendimento e relacionamento',
  'Produção de conteúdo',
  'Campanhas e aquisição',
  'Operações internas',
] as const;

const PUBLIC_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'outlook.com',
  'hotmail.com',
  'yahoo.com',
  'icloud.com',
]);

const SETUP_STEPS = [
  {
    id: 1,
    label: 'Workspace',
    description: 'Nome, equipe e foco inicial',
    section: 'Workspace',
    title: 'Defina a base operacional',
  },
  {
    id: 2,
    label: 'Equipe',
    description: 'Convites e domínio de e-mail',
    section: 'Equipe',
    title: 'Prepare o acesso da equipe',
  },
  {
    id: 3,
    label: 'Integrações',
    description: 'Conexões recomendadas pelo foco',
    section: 'Integrações',
    title: 'Escolha os primeiros sinais',
  },
] as const;

const INTEGRATIONS_BY_FOCUS: Record<FocusOption, readonly IntegrationOption[]> = {
  'Atendimento e relacionamento': [
    { key: 'whatsapp', name: 'WhatsApp Business', detail: 'Conversas e histórico de atendimento' },
    { key: 'instagram', name: 'Instagram', detail: 'Canais sociais e calendário editorial' },
    { key: 'meta-ads', name: 'Meta Ads', detail: 'Campanhas, públicos e performance' },
  ],
  'Produção de conteúdo': [
    { key: 'google-drive', name: 'Google Drive', detail: 'Briefings, arquivos e memória criativa' },
    { key: 'instagram', name: 'Instagram', detail: 'Canais sociais e calendário editorial' },
    { key: 'notion', name: 'Notion', detail: 'Planejamento, pautas e referências' },
  ],
  'Campanhas e aquisição': [
    { key: 'meta-ads', name: 'Meta Ads', detail: 'Campanhas, públicos e performance' },
    { key: 'google-ads', name: 'Google Ads', detail: 'Busca, mídia e conversões' },
    { key: 'rd-station', name: 'RD Station', detail: 'Leads, jornadas e automações' },
  ],
  'Operações internas': [
    { key: 'slack', name: 'Slack', detail: 'Fluxos de trabalho e alertas internos' },
    {
      key: 'google-workspace',
      name: 'Google Workspace',
      detail: 'Documentos, agenda e colaboração',
    },
    { key: 'webhooks', name: 'Webhooks', detail: 'Eventos internos e sistemas próprios' },
  ],
};

type TeamSizeOption = (typeof TEAM_SIZE_OPTIONS)[number];
type FocusOption = (typeof FOCUS_OPTIONS)[number];
type SetupStep = (typeof SETUP_STEPS)[number];
type SetupStepId = SetupStep['id'];

type IntegrationOption = {
  key: string;
  name: string;
  detail: string;
};

@Component({
  selector: 'app-business-onboarding',
  imports: [
    NgOptimizedImage,
    DatePipe,
    NgIcon,
    ReactiveFormsModule,
    NgpSelectPortal,
    UiBadge,
    UiButton,
    UiCheckbox,
    UiInput,
    UiLabel,
    UiOptionRow,
    UiPanelCard,
    UiSelect,
    UiSelectDropdown,
    UiSelectOption,
  ],
  templateUrl: './business-onboarding.page.html',
  styleUrl: './business-onboarding.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideIcons({
      heroPlus,
      heroCheck,
      heroTrash,
    }),
  ],
})
export class BusinessOnboardingPage {
  private readonly authStore = inject(AuthStore);
  private readonly businessApi = inject(CoreBusinessApiService);
  private readonly onboardingApi = inject(CoreOnboardingApiService);
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly posthog = inject(PostHogService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly toastr = inject(ToastrService);

  /**
   * Perfil > "Criar empresa" (W1-7): chega aqui com um business ATIVO na sessão e
   * `?mode=additional` na URL. Sem essa checagem, o passo 1 de `submit()` (linha ~249) veria
   * `this.business()` preenchido (o business ATUAL) e só avançaria de passo sem criar nada —
   * os passos seguintes (convites, `createInviteFromForm`) leem `this.business()` e acabariam
   * convidando gente pra organização ANTIGA, não pra nova que o usuário pediu pra criar. Usado só
   * no passo 1: uma vez criado, `setCurrentBusiness` troca a sessão pro business NOVO e
   * `finishSetup()` (passo 3) já vê `business()` não-nulo normalmente — sem duplicar criação.
   * `authGuard` libera essa rota com um business ativo só quando este mesmo query param existe
   * (ver auth.guard.ts::isAdditionalWorkspaceOnboarding) — os dois lados andam juntos.
   */
  private readonly isAdditionalWorkspace = this.route.snapshot.queryParamMap.get('mode') === 'additional';

  readonly business = this.authStore.business;
  readonly user = this.authStore.user;
  readonly discoveryWorkspaces = this.authStore.discoveryWorkspaces;
  readonly isJoiningWorkspace = signal<string | null>(null);
  readonly showsWorkspacePicker = computed(
    () => !this.business() && this.discoveryWorkspaces().length > 0,
  );
  readonly isSubmitting = signal(false);
  readonly isCreatingInvite = signal(false);
  readonly deletingInvitationIds = signal<string[]>([]);
  readonly currentStep = signal<SetupStepId>(1);
  readonly selectedTeamSize = signal<TeamSizeOption>(TEAM_SIZE_OPTIONS[0]);
  readonly selectedFocus = signal<FocusOption>(FOCUS_OPTIONS[0]);
  readonly selectedIntegrations = signal<string[]>(['whatsapp']);
  readonly availableRoles = signal<SetupRole[]>([]);
  readonly selectedInviteRoleIds = signal<string[]>([]);
  readonly createdInvitations = signal<InvitationSetupResponse[]>([]);
  readonly displayEmail = computed(() => this.user()?.email ?? '');
  readonly professionalEmailDomain = computed(() => readProfessionalDomain(this.displayEmail()));
  readonly canConfigureAutoInvite = computed(() => this.professionalEmailDomain() !== null);
  readonly logoSrc = '/automato-logo.svg';
  readonly setupSteps = SETUP_STEPS;
  readonly focusOptions = FOCUS_OPTIONS;
  readonly teamSizeOptions = TEAM_SIZE_OPTIONS;
  readonly currentStepMeta = computed(
    () => this.setupSteps.find((step) => step.id === this.currentStep()) ?? this.setupSteps[0],
  );
  readonly recommendedIntegrations = computed(() => INTEGRATIONS_BY_FOCUS[this.selectedFocus()]);

  readonly form = this.formBuilder.group({
    organizationName: [
      this.initialOrganizationName(),
      [Validators.required, Validators.minLength(2), Validators.maxLength(120)],
    ],
    teamSize: this.formBuilder.control<TeamSizeOption>(TEAM_SIZE_OPTIONS[0]),
    operatingFocus: this.formBuilder.control<FocusOption>(FOCUS_OPTIONS[0]),
    inviteEmail: ['', [Validators.email, Validators.maxLength(320)]],
    inviteRoleId: ['', Validators.required],
    autoInviteDomain: [this.professionalEmailDomain() ?? ''],
    autoInviteEnabled: [false],
  });

  private readonly domainSync = effect(() => {
    const domain = this.professionalEmailDomain();

    this.form.controls.autoInviteDomain.setValue(domain ?? '', { emitEvent: false });

    if (!domain) {
      this.form.controls.autoInviteEnabled.setValue(false, { emitEvent: false });
    }
  });

  private readonly setupDataSync = effect((onCleanup) => {
    const businessId = this.business()?.id;

    if (!businessId) {
      this.availableRoles.set([]);
      this.selectedInviteRoleIds.set([]);
      this.createdInvitations.set([]);
      return;
    }

    const rolesSubscription = this.onboardingApi.listRoles(businessId).subscribe({
      next: (roles) => this.applyAvailableRoles(roles),
      error: () => this.availableRoles.set([]),
    });

    const invitationsSubscription = this.onboardingApi.listInvitations(businessId).subscribe({
      next: (invitations) => this.createdInvitations.set(invitations),
      error: () => this.createdInvitations.set([]),
    });

    onCleanup(() => {
      rolesSubscription.unsubscribe();
      invitationsSubscription.unsubscribe();
    });
  });

  submit() {
    if (this.currentStep() === 1) {
      if (!this.validateWorkspaceStep()) {
        return;
      }

      this.posthog.posthog.capture('onboarding_workspace_step_completed', {
        organization_name: this.form.controls.organizationName.value.trim(),
        team_size: this.selectedTeamSize(),
        operating_focus: this.selectedFocus(),
      });

      // Modo additional (W1-7 — "Criar empresa" no Perfil): mesmo com um business ATIVO na
      // sessão, cria um DE VERDADE aqui (igual ao usuário novo) — não o business antigo. Isso
      // troca a sessão pro novo já no passo 1, então convites/integrações dos passos seguintes
      // (que leem `this.business()`) apontam pro business CERTO, não pro antigo.
      if (!this.business() || this.isAdditionalWorkspace) {
        this.createWorkspaceDraft();
        return;
      }

      this.currentStep.set(2);
      return;
    }

    if (this.currentStep() === 2) {
      this.currentStep.set(3);
      return;
    }

    this.finishSetup();
  }

  /**
   * Joins a domain-matched auto-invite candidate from the discovery response. `/onboarding/join`
   * only creates a pending invitation (never grants access by itself); when it comes back with a
   * token, accept it immediately — the user already proved ownership of the matching email by
   * signing in, so there's no reason to make them find the invite elsewhere. loadSession()
   * afterwards refreshes business()/isBusinessSetupRequired() from the now-attached session.
   */
  joinWorkspace(workspace: DiscoverableWorkspace) {
    this.isJoiningWorkspace.set(workspace.businessId);

    this.onboardingApi
      .joinWorkspace({ businessId: workspace.businessId })
      .pipe(
        switchMap((joined) => {
          const token = joined.invitation.token;
          return token ? this.onboardingApi.acceptInvitation({ token }) : of(null);
        }),
        switchMap(() => this.authStore.loadSession()),
        finalize(() => this.isJoiningWorkspace.set(null)),
      )
      .subscribe({
        next: () => {
          if (this.business()) {
            this.posthog.posthog.capture('onboarding_joined_workspace', {
              business_id: workspace.businessId,
            });
            void this.router.navigateByUrl('/');
            return;
          }

          this.toastr.success(
            'Convite enviado',
            'Peça para um administrador do workspace aprovar seu acesso.',
          );
        },
        error: () => this.toastr.error('Não foi possível entrar nesse workspace agora.'),
      });
  }

  goToStep(stepId: SetupStepId) {
    if (stepId <= this.currentStep()) {
      this.currentStep.set(stepId);
    }
  }

  previousStep() {
    const currentStep = this.currentStep();

    if (currentStep > 1) {
      this.currentStep.set((currentStep - 1) as SetupStepId);
    }
  }

  stepState(stepId: SetupStepId) {
    if (stepId < this.currentStep()) {
      return 'Concluído';
    }

    if (stepId === this.currentStep()) {
      return 'Atual';
    }

    return 'Próximo';
  }

  isStepComplete(stepId: SetupStepId) {
    return stepId < this.currentStep();
  }

  isStepCurrent(stepId: SetupStepId) {
    return stepId === this.currentStep();
  }

  setTeamSize(value: unknown) {
    if (isTeamSizeOption(value)) {
      this.selectedTeamSize.set(value);
      this.form.controls.teamSize.setValue(value);
    }
  }

  setOperatingFocus(value: unknown) {
    if (isFocusOption(value)) {
      this.selectedFocus.set(value);
      this.form.controls.operatingFocus.setValue(value);
      this.selectedIntegrations.set([INTEGRATIONS_BY_FOCUS[value][0].key]);
    }
  }

  toggleIntegration(key: string) {
    const selectedIntegrations = this.selectedIntegrations();

    if (selectedIntegrations.includes(key)) {
      this.selectedIntegrations.set(selectedIntegrations.filter((item) => item !== key));
      return;
    }

    this.selectedIntegrations.set([...selectedIntegrations, key]);
  }

  isIntegrationSelected(key: string) {
    return this.selectedIntegrations().includes(key);
  }

  setInviteRole(value: unknown) {
    if (typeof value === 'string') {
      this.selectedInviteRoleIds.set(value ? [value] : []);
      this.form.controls.inviteRoleId.setValue(value);
    }
  }

  isInviteRoleSelected(roleId: string) {
    return this.selectedInviteRoleIds().includes(roleId);
  }

  selectedInviteRoleName() {
    const roleId = this.selectedInviteRoleIds()[0];
    return this.availableRoles().find((role) => role.id === roleId)?.name ?? 'Selecionar role';
  }

  createInviteFromForm(event?: Event) {
    event?.preventDefault();
    this.form.controls.inviteEmail.markAsTouched();
    this.form.controls.inviteRoleId.markAsTouched();

    const business = this.business();
    const email = this.form.controls.inviteEmail.value.trim();
    const roleIds = this.selectedInviteRoleIds();

    if (!business) {
      this.toastr.error('Crie o workspace antes de enviar convites.');
      return;
    }

    if (!email || this.form.controls.inviteEmail.invalid) {
      this.toastr.error('Informe um e-mail válido para criar o convite.');
      return;
    }

    if (roleIds.length === 0) {
      this.toastr.error('Selecione pelo menos uma função para o convite.');
      return;
    }

    this.isCreatingInvite.set(true);
    this.onboardingApi
      .createInvitation({
        businessId: business.id,
        email,
        roleIds,
        teams: [],
        expiresInDays: 7,
      })
      .pipe(finalize(() => this.isCreatingInvite.set(false)))
      .subscribe({
        next: (invitation) => {
          this.posthog.posthog.capture('onboarding_team_invitation_created', {
            invitation_count: this.createdInvitations().length + 1,
          });
          this.createdInvitations.update((invitations) => [
            invitation,
            ...invitations.filter((item) => item.id !== invitation.id),
          ]);
          this.form.controls.inviteEmail.setValue('');
          this.form.controls.inviteEmail.markAsPristine();
          this.form.controls.inviteEmail.markAsUntouched();
          this.toastr.success('Convite criado', `${email} foi adicionado à lista de convites.`);
        },
        // O toast de erro é do interceptor HTTP (mapeia o code — ex.: `billing_capacity_exceeded` →
        // "Limite do plano atingido" + CTA "Ver planos"). Handler vazio só consome o erro; o
        // `finalize` acima já reabilita o form. Um toast genérico aqui duplicaria/mascararia o instrutivo.
        error: () => {},
      });
  }

  revokeInvite(invitation: InvitationSetupResponse) {
    if (invitation.status !== 'pending' || this.isDeletingInvite(invitation.id)) {
      return;
    }

    this.deletingInvitationIds.update((ids) => [...ids, invitation.id]);

    this.onboardingApi
      .revokeInvitation(invitation.id)
      .pipe(finalize(() => this.removeDeletingInvitationId(invitation.id)))
      .subscribe({
        next: (revokedInvitation) => {
          this.createdInvitations.update((invitations) =>
            invitations.map((item) => (item.id === revokedInvitation.id ? revokedInvitation : item)),
          );
          this.toastr.success('Convite removido', `${invitation.email} foi revogado.`);
        },
        error: () => {
          this.toastr.error('Não foi possível remover o convite agora.');
        },
      });
  }

  isDeletingInvite(invitationId: string) {
    return this.deletingInvitationIds().includes(invitationId);
  }

  invitationStatusLabel(status: string) {
    if (status === 'pending') return 'Pendente';
    if (status === 'accepted') return 'Aceito';
    if (status === 'expired') return 'Expirado';
    if (status === 'revoked') return 'Revogado';
    return status;
  }

  private finishSetup() {
    if (!this.validateWorkspaceStep()) {
      this.currentStep.set(1);
      return;
    }

    const business = this.business();

    // Sempre falso aqui em modo additional: o passo 1 (submit()) já criou o business novo e
    // trocou a sessão pra ele — business() já É o novo, não o antigo. Chamar createWorkspaceDraft
    // de novo aqui duplicaria a organização.
    if (!business) {
      this.createWorkspaceDraft();
      return;
    }

    this.completeExistingBusiness(business.id);
  }

  private validateWorkspaceStep() {
    this.form.controls.organizationName.markAsTouched();

    if (this.form.controls.organizationName.invalid) {
      this.toastr.error('Informe o nome da organização para continuar.');
      return false;
    }

    return true;
  }

  private createWorkspaceDraft() {
    this.isSubmitting.set(true);
    this.form.disable();
    this.onboardingApi
      .createWorkspace({
        name: this.organizationName(),
      })
      .pipe(
        switchMap(({ business, membership }) =>
          this.authStore.loadSession().pipe(map(() => ({ business, membership }))),
        ),
      )
      .pipe(finalize(() => this.finishSubmitting()))
      .subscribe({
        next: ({ business, membership }) => {
          this.authStore.setCurrentBusiness(
            {
              id: business.id,
              name: business.name,
              status: business.status,
              providerOrganizationId: null,
            },
            membership.id,
          );
          this.currentStep.set(2);
        },
        error: () => {
          this.toastr.error('Não foi possível criar a organização agora.');
        },
      });
  }

  private completeExistingBusiness(businessId: string) {
    this.isSubmitting.set(true);
    this.form.disable();
    this.businessApi
      .completeSetup(businessId, {
        name: this.organizationName(),
        status: 'active',
      })
      .pipe(
        switchMap((updated) =>
          this.configureAutoInviteDomain(businessId).pipe(map(() => updated)),
        ),
      )
      .pipe(finalize(() => this.finishSubmitting()))
      .subscribe({
        next: (updated) => {
          this.posthog.posthog.capture('onboarding_completed', {
            selected_integrations: this.selectedIntegrations(),
            invitation_count: this.createdInvitations().length,
          });
          this.authStore.updateCurrentBusiness({
            name: updated.name,
            status: updated.status,
          });
          void this.router.navigateByUrl('/');
        },
        error: () => {
          this.toastr.error('Não foi possível concluir a configuração agora.');
        },
      });
  }

  private finishSubmitting() {
    this.form.enable();
    this.isSubmitting.set(false);
  }

  private organizationName() {
    return this.form.controls.organizationName.value.trim();
  }

  private configureAutoInviteDomain(businessId: string): Observable<unknown> {
    const domain = this.professionalEmailDomain();

    if (!domain || !this.form.controls.autoInviteEnabled.value) {
      return of(null);
    }

    return this.onboardingApi.createEmailDomain({
      businessId,
      domain,
      autoInviteEnabled: true,
      includeSubdomains: false,
      defaultExpiresInDays: 7,
      roleIds: this.selectedInviteRoleIds(),
      teams: [],
    });
  }

  private applyAvailableRoles(roles: SetupRole[]) {
    this.availableRoles.set(roles);
    this.ensureDefaultInviteRole(roles);
  }

  private ensureDefaultInviteRole(roles: SetupRole[]) {
    if (this.selectedInviteRoleIds().length > 0) {
      return;
    }

    const defaultRole =
      roles.find((role) => role.key === 'viewer') ??
      roles.find((role) => role.key === 'member') ??
      roles[0];

    if (defaultRole) {
      this.selectedInviteRoleIds.set([defaultRole.id]);
      this.form.controls.inviteRoleId.setValue(defaultRole.id);
    }
  }

  private removeDeletingInvitationId(invitationId: string) {
    this.deletingInvitationIds.update((ids) => ids.filter((id) => id !== invitationId));
  }

  private initialOrganizationName() {
    const name = this.authStore.business()?.name ?? '';
    return name.includes("'s Workspace") ? '' : name;
  }
}

function isTeamSizeOption(value: unknown): value is TeamSizeOption {
  return typeof value === 'string' && TEAM_SIZE_OPTIONS.includes(value as TeamSizeOption);
}

function isFocusOption(value: unknown): value is FocusOption {
  return typeof value === 'string' && FOCUS_OPTIONS.includes(value as FocusOption);
}

function readProfessionalDomain(email: string) {
  const domain = email.split('@')[1]?.trim().toLowerCase();

  if (!domain || PUBLIC_EMAIL_DOMAINS.has(domain)) {
    return null;
  }

  return domain;
}
